/** @file syncImages: makes `doc.images` exactly the images the puppet references (sheets and entry slots). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { ImageMeta, PuppetDoc } from './puppetDoc';

function referenced(puppet: Puppet): Set<string> {
  const ids = new Set(Object.values(puppet.sheets).map((s) => s.image));
  const entries: FacetEntry[] = puppet.drawables.flatMap((d) => (d.kind === 'impostor' || d.kind === 'effect' ? (d.facets ?? []) : []));
  for (const entry of entries) for (const image of Object.values(entry.slots)) ids.add(image);
  return ids;
}

/**
 * Drops the images nothing references and adds referenced ones from `available` (the server's store); an image in
 * neither throws `NOT_FOUND` at `/images/<id>`. Keeps `doc.images`' own copy (with its `fit`) when it has one.
 */
export function syncImages(doc: PuppetDoc, available: Readonly<Record<string, ImageMeta>>): void {
  const out: Record<string, ImageMeta> = {};
  for (const id of [...referenced(doc.puppet)].sort()) {
    out[id] = doc.images[id] ?? available[id]
      ?? throwError(ERROR_CODES.NOT_FOUND, { image: id }, { path: `/images/${id}`, hint: 'upload the image first' });
  }
  doc.images = out;
}
